'use client';
import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, Spinner, Container, Row, Col } from 'react-bootstrap';
import Button from 'components/base/Button';
import Badge from 'components/base/Badge';
import ConfirmBookingModal from 'components/modals/ConfirmBookingModal';
import SuccessModal from 'components/modals/SuccessModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendar,
  faClock,
  faMapPin,
  faPoundSign,
  faUserMd,
  faArrowLeft,
  faCoffee,
  faRoad,
  faCapsules,
  faPrescriptionBottleMedical 
} from '@fortawesome/free-solid-svg-icons';
import { getShiftById } from 'features/shift/shiftApi';
import { bookShift, getMyBookings } from 'features/booking/bookingApi';

const ShiftDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [shift, setShift] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    const fetchShift = async () => {
      try {
        const shiftData = await getShiftById(id!);
        const myBookings = await getMyBookings();
        const isBooked = myBookings?.some(
          (b: any) => b.shift_id === parseInt(id!) && b.status === 'confirmed'
        );
        setShift({ ...shiftData, is_booked: isBooked });
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchShift();
  }, [id, location.key]);

  const handleBook = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmBook = async () => {
    try {
      setBooking(true);
      await bookShift(shift.id);

      // update UI instantly
      setShift((prev: any) => ({ ...prev, is_booked: true }));
      setShowConfirmModal(false);
      setShowSuccessModal(true);
    } catch (err: any) {
      alert(err.message);
    } finally {
      setBooking(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-8">
        <div className="text-center">
          <Spinner animation="border" role="status" className="text-primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (!shift) {
    return (
      <Container className="py-8">
        <p className="text-center text-body-tertiary fs-5">Shift not found</p>
      </Container>
    );
  }

  return (
    <Container className="py-0 py-sm-2  ">
      {/* 🔹 Header Section */}
      <div className="mb-5">
        <Button 
          color="secondary" 
          size="sm" 
          onClick={() => navigate(-1)}
          className="mb-3"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back
        </Button>

        <div className="d-flex align-items-start justify-content-between gap-3 mb-4">
          <div>
            <p className="text-body-tertiary text-uppercase fs-9 fw-bold mb-2">
              Shift Details
            </p>
            <h3 className="text-body-emphasis mb-2 fw-bold">
              {new Date(shift.date).toLocaleDateString('en-GB', {
                weekday: 'long',
                day: 'numeric',
                month: 'long'
              })}
            </h3>
            <p className="text-body-tertiary fs-8 mb-2">
              {shift.location} • {shift.locum_role}
            </p>
            {shift.distance_miles !== null && shift.distance_miles !== undefined && (
              <p className="text-body-tertiary fs-8 mb-0">
                <FontAwesomeIcon icon={faRoad} className="me-2" />
                {shift.distance_miles === 0 ? 'Same location' : `${shift.distance_miles} miles away`}
              </p>
            )}
          </div>

          <Badge variant="phoenix" bg="success" className="fs-9 px-3 py-2">
            {shift.status}
          </Badge>
        </div>
      </div>

      {/* 🔹 Main Content */}
      <Row className="g-4">
        {/* Left Column - Main Details */}
        <Col lg={8}>
          {/* Shift Details Card */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Body className="p-5">
              <h5 className="text-body-emphasis fw-bold mb-4 fs-6">Shift Information</h5>

              <div className="row g-4">
                {/* Date & Time */}

                <div className="col-md-6">
                    <small className="text-body-tertiary text-uppercase fw-bold fs-9 d-block mb-2">
                       <FontAwesomeIcon icon={faPrescriptionBottleMedical } className="me-2 text-primary" />
                      Pharmacy Name
                    </small>
                    <p className="text-body-emphasis fs-9 fw-semibold mb-0">
                      {shift.pharmacy_name}
                    </p>
                  </div>

                <div className="col-md-6">
                  <div>
                    <small className="text-body-tertiary text-uppercase fw-bold fs-9 d-block mb-2">
                      <FontAwesomeIcon icon={faCalendar} className="me-2 text-primary" />
                      Date
                    </small>
                    <p className="text-body-emphasis fs-9 fw-semibold mb-0">
                      {new Date(shift.date).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                </div>

                {/* Time */}
                <div className="col-md-6">
                  <div>
                    <small className="text-body-tertiary text-uppercase fw-bold fs-9 d-block mb-2">
                      <FontAwesomeIcon icon={faClock} className="me-2 text-info" />
                      Time
                    </small>
                    <p className="text-body-emphasis fs-9 fw-semibold mb-0">
                      {shift.start_time.slice(0, 5)} - {shift.end_time.slice(0, 5)}
                    </p>
                  </div>
                </div>

                {/* Location */}
                <div className="col-md-6">
                  <div>
                    <small className="text-body-tertiary text-uppercase fw-bold fs-9 d-block mb-2">
                      <FontAwesomeIcon icon={faMapPin} className="me-2 text-danger" />
                      Location
                    </small>
                    <p className="text-body-emphasis fs-9 fw-semibold mb-0">
                      {shift.location}
                    </p>
                  </div>
                </div>

                {/* Postcode */}
                {shift.postcode && (
                  <div className="col-md-6">
                    <div>
                      <small className="text-body-tertiary text-uppercase fw-bold fs-9 d-block mb-2">
                        <FontAwesomeIcon icon={faMapPin} className="me-2 text-danger" />
                        Postcode
                      </small>
                      <p className="text-body-emphasis fs-9 fw-semibold mb-0">
                        {shift.postcode}
                      </p>
                    </div>
                  </div>
                )}

                {/* Role */}
                <div className="col-md-6">
                  <div>
                    <small className="text-body-tertiary text-uppercase fw-bold fs-9 d-block mb-2">
                      <FontAwesomeIcon icon={faUserMd} className="me-2 text-success" />
                      Position
                    </small>
                    <p className="text-body-emphasis fs-9 fw-semibold mb-0">
                      {shift.locum_role}
                    </p>
                  </div>
                </div>

                {/* Pay Rate */}
                <div className="col-md-6">
                  <div>
                    <small className="text-body-tertiary text-uppercase fw-bold fs-9 d-block mb-2">
                      <FontAwesomeIcon icon={faPoundSign} className="me-2 text-warning" />
                      Pay Rate
                    </small>
                    <p className="text-body-emphasis fs-9 fw-bold mb-0">
                      £{shift.pay_rate} <span className="fs-8 fw-semibold text-body-tertiary">/hour</span>
                    </p>
                  </div>
                </div>

                {/* Lunch Break */}
                {shift.lunch_break_minutes !== null && shift.lunch_break_minutes !== undefined && (
                  <div className="col-md-6">
                    <div>
                      <small className="text-body-tertiary text-uppercase fw-bold fs-9 d-block mb-2">
                        <FontAwesomeIcon icon={faCoffee} className="me-2 text-brown" />
                        Lunch Break
                      </small>
                      <p className="text-body-emphasis fs-9 fw-semibold mb-0">
                        {shift.lunch_break_minutes === 0 ? 'No lunch break' : `${shift.lunch_break_minutes} minutes`}
                      </p>
                    </div>
                  </div>
                )}

                <div className="col-md-6">
                  <div>
                    <small className="text-body-tertiary text-uppercase fw-bold fs-9 d-block mb-2">
                      <FontAwesomeIcon icon={faRoad} className="me-2 text-warning" />
                      Travel Expenses
                    </small>
                    <p className="text-body-emphasis fs-9 fw-bold mb-0">
                      {shift.travel_expenses}
                    </p>
                  </div>
                </div>

                <div className="col-md-6">
                  <div>
                    <small className="text-body-tertiary text-uppercase fw-bold fs-9 d-block mb-2">
                      <FontAwesomeIcon icon={faCapsules} className="me-2 text-warning" />
                      Dispensing System
                    </small>
                    <p className="text-body-emphasis fs-9 fw-bold mb-0">
                      {shift.dispensing_system}
                    </p>
                  </div>
                </div>


              </div>
            </Card.Body>
          </Card>

          {/* Shift Description */}
          {shift.summary && (
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body className="p-5">
                <h5 className="text-body-emphasis fw-bold mb-3 fs-6">About This Shift</h5>
                <p className="text-body-tertiary fs-8 lh-lg mb-0">
                  {shift.summary}
                </p>
              </Card.Body>
            </Card>
          )}

          {/* Pharmacy Information */}
          {/* {shift.pharmacy_name && (
            <Card className="border-0 shadow-sm mb-5">
              <Card.Body className="p-5">
                <h5 className="text-body-emphasis fw-bold mb-4 fs-6">
                  <FontAwesomeIcon icon={faBuilding} className="me-2 text-primary" />
                  Pharmacy Information
                </h5>

                <div className="row g-4">
                  <div className="col-md-6">
                    <small className="text-body-tertiary text-uppercase fw-bold fs-9 d-block mb-2">
                      Pharmacy Name
                    </small>
                    <p className="text-body-emphasis fs-9 fw-semibold mb-0">
                      {shift.pharmacy_name}
                    </p>
                  </div>

                  {shift.contact_person && (
                    <div className="col-md-6">
                      <small className="text-body-tertiary text-uppercase fw-bold fs-9 d-block mb-2">
                        <FontAwesomeIcon icon={faUser} className="me-1" />
                        Contact Person
                      </small>
                      <p className="text-body-emphasis fs-9 fw-semibold mb-0">
                        {shift.contact_person}
                      </p>
                    </div>
                  )}

                  {shift.pharmacy_location && (
                    <div className="col-md-6">
                      <small className="text-body-tertiary text-uppercase fw-bold fs-9 d-block mb-2">
                        <FontAwesomeIcon icon={faMapPin} className="me-1" />
                        Location
                      </small>
                      <p className="text-body-emphasis fs-9 fw-semibold mb-0">
                        {shift.pharmacy_location}
                      </p>
                    </div>
                  )}

                  {shift.phone_number && (
                    <div className="col-md-6">
                      <small className="text-body-tertiary text-uppercase fw-bold fs-9 d-block mb-2">
                        <FontAwesomeIcon icon={faPhone} className="me-1" />
                        Phone Number
                      </small>
                      <p className="text-body-emphasis fs-9 fw-semibold mb-0">
                        {shift.phone_number}
                      </p>
                    </div>
                  )}

                  {shift.pharmacy_address && (
                    <div className="col-md-6">
                      <small className="text-body-tertiary text-uppercase fw-bold fs-9 d-block mb-2">
                        Address
                      </small>
                      <p className="text-body-emphasis fs-9 fw-semibold mb-0">
                        {shift.pharmacy_address}
                      </p>
                    </div>
                  )}

                  {shift.license_number && (
                    <div className="col-md-6">
                      <small className="text-body-tertiary text-uppercase fw-bold fs-9 d-block mb-2">
                        License Number
                      </small>
                      <p className="text-body-emphasis fs-9 font-monospace fw-semibold mb-0">
                        {shift.license_number}
                      </p>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          )} */}
        </Col>

        {/* Right Column - CTA */}
        <Col lg={4}>
          <Card className="border-0 shadow-sm sticky-top" style={{ top: '2rem' }}>
            <Card.Body className="p-5">
              <div className="mb-4">
                <h6 className="text-body-tertiary text-uppercase fw-bold fs-9 mb-3">
                  Ready to Work?
                </h6>
                <p className="text-body-tertiary fs-9 mb-0">
                  {shift.is_booked
                    ? 'You have already booked this shift.'
                    : 'Book this shift now to secure your position at the pharmacy.'}
                </p>
              </div>

              {shift.status === 'cancelled' || shift.status === 'closed' ? (
                <Button color="secondary" size="lg" disabled className="w-100">
                  <FontAwesomeIcon icon={faCalendar} className="me-2" />
                  {shift.status === 'cancelled' ? 'Cancelled' : 'Closed'}
                </Button>
              ) : shift.is_booked ? (
                <Button color="secondary" size="lg" disabled className="w-100">
                  <FontAwesomeIcon icon={faCalendar} className="me-2" />
                  Booked
                </Button>
              ) : (
                <Button
                  variant="phoenix-primary"
                  size="lg"
                  disabled={booking}
                  onClick={handleBook}
                  className="w-100"
                >
                  {booking ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Booking...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCalendar} className="me-2" />
                      Book Now
                    </>
                  )}
                </Button>
              )}

              {/* Info Box */}
              <div className="mt-4 p-3 bg-body-secondary rounded-3">
                <small className="text-body-tertiary fs-9">
                  <strong>📋 Note:</strong> Once booked, you'll be notified with pharmacy details and further instructions.
                </small>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 🔹 Confirmation Modal */}
      <ConfirmBookingModal
        show={showConfirmModal}
        itemTitle={`${new Date(shift.date).toLocaleDateString('en-GB')} - ${shift.location}`}
        onConfirm={handleConfirmBook}
        onCancel={() => setShowConfirmModal(false)}
        isLoading={booking}
      />

      {/* 🔹 Success Modal */}
      <SuccessModal
        show={showSuccessModal}
        title="Booking Confirmed!"
        message="Your shift has been booked successfully. Check your bookings for details."
        onClose={() => setShowSuccessModal(false)}
        closeLabel="Close"
        closeDelay={2000}
      />
    </Container>
  );
};

export default ShiftDetailsPage;
